import "./RearrangeNoteGroups.scss";
import "../../../../components/ModalContent/ModalComponents.scss";
import { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fetcher } from "../../../../utils/fetcher";
import { useTranslation } from "react-i18next";

// Sortable Item Component
function SortableNoteGroup({ group, hexToRgba }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: hexToRgba(group.color, 0.8),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rearrange-notegroup-item"
      {...attributes}
      {...listeners}
    >
      <div className="drag-icon">
        <span className="material-symbols-outlined">drag_indicator</span>
      </div>
      <div>{group.name}</div>
    </div>
  );
}

function RearrangeNoteGroups({ noteGroups, closeRearrangeNoteGroups, onSave }) {
  const { t } = useTranslation();
  const [items, setItems] = useState(noteGroups);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const saveOrder = async (newItems) => {
    try {
      setIsSaving(true);

      // Prepare the payload for the reorder endpoint
      const updates = newItems.map((item, index) => ({
        _id: item._id,
        sortOrder: index,
      }));

      // Call the reorder endpoint
      await fetcher.put("/api/noteGroups/reorder", updates);

      // Refresh parent immediately after save
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("Error reordering note groups:", error);
      // alert("Failed to save the new order. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    setItems(noteGroups);
  }, [noteGroups]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);

      setItems(newItems);

      // Automatically save the new order and refresh parent
      saveOrder(newItems);
    }
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="rearrange-note-groups-content">
      <div className="modal-title">
        <div>{t("notes.modals.rearrangeGroups.title")}</div>
      </div>
      <div className="modal-form">
        <div className="modal-notegroups-list-wrapper">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((group) => (
                <SortableNoteGroup
                  key={group.id}
                  group={group}
                  hexToRgba={hexToRgba}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <div className="modal-form-buttons">
          <button
            className="modal-button cancel"
            onClick={() => {
              closeRearrangeNoteGroups();
            }}
            disabled={isSaving}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RearrangeNoteGroups;
